import React, { useState, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
} from "chart.js";
import {
  ExclamationTriangle,
  Copy,
  ChevronUp,
  ChevronDown,
  ChevronExpand,
  ArrowClockwise,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";

import styles from "./Academichealthinsights.module.css";
import { useSelector } from "react-redux";

ChartJS.register(ArcElement, Tooltip, Legend, DoughnutController);

/* ============================================================================
 * utils/riskUtils.js
 * ==========================================================================*/
const RISK_BUCKETS = {
  noRisk: {
    label: "No Risk (8 - 10)",
    color: "#22C55E", // solid (legend swatches, fallback)
    gradient: ["#10b981", "#6ee7b7"], // matches --gradient-green-pair
    badgeClass: styles.badgeNoRisk,
  },
  mediumRisk: {
    label: "Medium Risk (5 - 7.9)",
    color: "#F97316",
    gradient: ["#f97316", "#fdba74"], // matches --gradient-orange-pair
    badgeClass: styles.badgeMediumRisk,
  },
  highRisk: {
    label: "High Risk (0 - 4.9)",
    color: "#DC2626",
    gradient: ["#ef4444", "#fca5a5"], // matches --gradient-red-pair
    badgeClass: styles.badgeHighRisk,
  },
};

const getRiskBucket = (score) => {
  if (score >= 8) return "noRisk";
  if (score >= 5) return "mediumRisk";
  return "highRisk";
};

/* ============================================================================
 * chart/plugins.js — reusable Chart.js plugins
 * ==========================================================================*/
export const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const text = String(opts.text ?? "");
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.font = `bold ${opts.fontSize ?? 28}px ${opts.fontFamily ?? "system-ui, sans-serif"}`;
    ctx.fillStyle = opts.color ?? "#1E293B";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cx, cy);
    ctx.restore();
  },
};

export const segmentLabelPlugin = {
  id: "segmentLabel",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta) return;
    meta.data.forEach((arc, i) => {
      const value = chart.data.datasets[0].data[i];
      if (!value) return;
      const { x, y } = arc.tooltipPosition();
      ctx.save();
      ctx.font = `bold ${opts.fontSize ?? 16}px ${opts.fontFamily ?? "system-ui, sans-serif"}`;
      ctx.fillStyle = opts.color ?? "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value), x, y);
      ctx.restore();
    });
  },
};

/* ============================================================================
 * components/RiskBadge.jsx
 * ==========================================================================*/
export const RiskBadge = ({ score }) => {
  const bucket = getRiskBucket(score);
  const { label, badgeClass } = RISK_BUCKETS[bucket];
  return (
    <span
      className={`${styles.badge} ${badgeClass}`}
      aria-label={`Academic health score ${score}, ${label}`}
    >
      {score}
    </span>
  );
};

/* ============================================================================
 * components/RiskDonutChart.jsx
 * Donut segments are individually clickable.
 *
 * Props:
 *   summary        → { noRisk, mediumRisk, highRisk }
 *   onSegmentClick → (riskKey) => void   "noRisk" | "mediumRisk" | "highRisk"
 * ==========================================================================*/

// Matches dataset order in the donut (index 0,1,2)
const RISK_KEYS_BY_INDEX = ["noRisk", "mediumRisk", "highRisk"];
// Build a CanvasGradient Chart.js can paint a segment with
const makeGradient = (chart, [from, to]) => {
  const { ctx, chartArea } = chart;
  if (!chartArea) return from; // first paint — chart not laid out yet
  const g = ctx.createLinearGradient(
    chartArea.left,
    chartArea.top,
    chartArea.right,
    chartArea.bottom,
  );
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
};
export const RiskDonutChart = ({ summary, onSegmentClick, activeRiskKey = null, }) => {
  const { noRisk = 0, mediumRisk = 0, highRisk = 0 } = summary || {};
  const total = noRisk + mediumRisk + highRisk;
  // index of the active segment (0 = noRisk, 1 = mediumRisk, 2 = highRisk)
  const activeIndex = activeRiskKey
    ? RISK_KEYS_BY_INDEX.indexOf(activeRiskKey)
    : -1;
  const data = {
    labels: ["No Risk", "Medium Risk", "High Risk"],
    datasets: [
      {
        data: [noRisk, mediumRisk, highRisk],
        backgroundColor: (ctx) => {
          const key = RISK_KEYS_BY_INDEX[ctx.dataIndex];
          return makeGradient(ctx.chart, RISK_BUCKETS[key].gradient);
        },
        borderWidth: 0,
        hoverOffset: 6,
        // pop the active segment out by default
        offset: (ctx) => (ctx.dataIndex === activeIndex ? 18 : 0),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    // ---- Segment click ----
    onClick: (_event, elements) => {
      if (elements && elements.length > 0) {
        const idx = elements[0].index;
        const riskKey = RISK_KEYS_BY_INDEX[idx];
        if (riskKey) onSegmentClick?.(riskKey);
      }
    },
    // ---- Pointer cursor on segments ----
    onHover: (event, elements) => {
      const target = event?.native?.target;
      if (target) {
        target.style.cursor = elements.length > 0 ? "pointer" : "default";
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
      centerText: { text: String(total), fontSize: 28, color: "#1E293B" },
      segmentLabel: { fontSize: 16, color: "#fff" },
    },
  };

  return (
    <div className={styles.chartWrap}>
      <div className={styles.chartCanvasWrap}>
        <Doughnut
          data={data}
          options={options}
          plugins={[centerTextPlugin, segmentLabelPlugin]}
        />
      </div>

      <div className={styles.legend}>
        {Object.entries(RISK_BUCKETS).map(([key, b]) => (
          <div key={key} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ backgroundColor: b.color }}
            />
            <span>{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================================================================
 * components/StudentPerformanceTable.jsx
 * ==========================================================================*/
const COLUMNS = [
  { key: "name", label: "Student Name", isName: true },
  { key: "overallExamScore", label: "Overall Exam Score" },
  { key: "attendancePercent", label: "Attendance (%)" },
  { key: "assessmentRemedialScore", label: "Assessment & Remedial Score (%)" },
  { key: "applicationUsagePercent", label: "Application Usage (%)" },
  { key: "academicHealthScore", label: "Academic Health Score" },
];

const SortIcon = ({ direction }) => {
  if (direction === "asc")
    return <ChevronUp size={12} className={styles.sortIcon} />;
  if (direction === "desc")
    return <ChevronDown size={12} className={styles.sortIcon} />;
  return (
    <ChevronExpand
      size={12}
      className={`${styles.sortIcon} ${styles.sortIconIdle}`}
    />
  );
};

export const StudentPerformanceTable = ({ students, onCopyPhone,handleClick }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedStudents = useMemo(() => {
    if (!sortKey) return students;
    return [...students].sort((a, b) => {
      const av = a[sortKey],
        bv = b[sortKey];
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [students, sortKey, sortDir]);

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={`${styles.th} ${col.isName ? styles.thName : ""}`}
              >
                <button
                  type="button"
                  onClick={() => handleSort(col.key)}
                  className={styles.sortBtn}
                >
                  <span>{col.label}</span>
                  <SortIcon direction={sortKey === col.key ? sortDir : null} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedStudents.map((s, i) => (
            <tr key={s.id} className={i % 2 === 1 ? styles.rowAlt : styles.row}>
              <td className={styles.td}>
                <div className={styles.studentName}>
                  <p
                    className={styles.absenteeName}
                    onClick={() => handleClick?.(s.id)}
                    title={s.name}
                  >
                    {s.name}
                  </p>
                </div>
                {/* <div className={styles.studentPhoneRow}>
                  <span>{s.phone}</span>
                  <button
                    type="button"
                    onClick={() => onCopyPhone(s.phone)}
                    className={styles.copyBtn}
                    aria-label={`Copy phone number ${s.phone}`}
                  >
                    <Copy size={14} />
                  </button>
                </div> */}
              </td>
              <td className={styles.td}>{s.overallExamScore}</td>
              <td className={styles.td}>{s.attendancePercent}</td>
              <td className={styles.td}>{s.assessmentRemedialScore}</td>
              <td className={styles.td}>{s.applicationUsagePercent}</td>
              <td className={styles.td}>
                <RiskBadge score={s.academicHealthScore} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ============================================================================
 * State sub-components
 * ==========================================================================*/
const SkeletonState = () => (
  <div className={styles.skeletonBody}>
    <div className={styles.skeletonChart}>
      <div className={styles.skeletonCircle} />
      <div className={styles.skeletonBar} />
    </div>
    <div>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={styles.skeletonRow} />
      ))}
    </div>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className={styles.errorState}>
    <ExclamationTriangle size={40} className={styles.errorIcon} />
    <p className={styles.errorText}>
      Something went wrong while loading insights.
    </p>
    {onRetry && (
      <button type="button" onClick={onRetry} className={styles.retryBtn}>
        <ArrowClockwise size={16} /> Retry
      </button>
    )}
  </div>
);

const EmptyState = () => (
  <div className={styles.emptyState}>
    No students assigned to your class and section yet.
  </div>
);

/* ============================================================================
 * components/AcademicHealthInsights.jsx  ← PRESENTATIONAL (no API)
 *
 * Props:
 *   data    — { summary: {...}, students: [...] }  (mapped UI shape, from parent)
 *   status  — "idle" | "loading" | "success" | "error"
 *   onRetry — () => void                           (called when user clicks Retry)
 * ==========================================================================*/
/* ============================================================================
 * components/AcademicHealthInsights.jsx
 *
 * Props:
 *   data           — { summary: {...}, students: [...] }
 *   status         — "idle" | "loading" | "success" | "error"
 *   onSegmentClick — (riskKey) => void   "noRisk" | "mediumRisk" | "highRisk"
 *   onRetry        — () => void
 * ==========================================================================*/
const AcademicHealthInsights = ({
  data,
  status = "loading",
  layout = "row",
  notePosition = "header",
  onSegmentClick,
  handleClick,
  onRetry,
  note,
  title = "",
  activeRiskKey = "noRisk",
}) => {
  const handleCopyPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success("Phone number copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  const isSplit = layout === "split";
  // Empty only when the class/section has no students at all. The student
  // list is filtered by the selected risk segment (default: High risk), so an
  // empty list with a non-zero summary must still show the donut.
  const isEmpty =
    data &&
    Array.isArray(data.students) &&
    data.students.length === 0 &&
    !(data.summary && Number(data.summary.total) > 0);
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.headerIcon} aria-hidden="true">
          <ExclamationTriangle />
        </span>
        <h2 className="chartTitle">
          {title || t.AcademicHealthInsights || "Academic Health Score Insights"}
        </h2>
        {note && notePosition === "header" && (
          <div className={styles.noteBanner}>
            <strong>Note:</strong> {note}
          </div>
        )}
      </div>

      {(status === "idle" || status === "loading") && <SkeletonState />}
      {status === "error" && <ErrorState onRetry={onRetry} />}
      {status === "success" && isEmpty && <EmptyState />}
      {status === "success" && data && !isEmpty && (
        <div className={styles.body}>
          <div className={styles.chartCol}>
            <RiskDonutChart
              summary={data.summary}
              onSegmentClick={onSegmentClick} // ← pass through
            />
          </div>
          <div className={styles.tableCol}>
            {Array.isArray(data.students) && data.students.length === 0 ? (
              <div className={styles.emptyState}>
                {t.noStudentsInThisCategory ||
                  "No students in this category. Click another segment of the chart."}
              </div>
            ) : (
              <StudentPerformanceTable
                students={data.students}
                onCopyPhone={handleCopyPhone}
                handleClick={handleClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicHealthInsights;
